import React, { useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  FileSpreadsheet,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { MetaUnileverRow } from './types';
import {
  EXPECTED_HEADERS,
  finalizeMetaValue,
  finalizeMonthValue,
  finalizeYearValue,
  makeLocalGuid,
  normalizeText,
  normalizeKey,
  parseMonthNumber,
  parseYearNumber,
  REQUIRED_IMPORT_HEADERS,
} from './utils';

type ParsedImportRow = MetaUnileverRow & {
  rowNumber: number;
  valid: boolean;
  reason?: string;
};

type ImportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImport: (rows: MetaUnileverRow[]) => Promise<boolean>;
};

function parseFile(file: File): Promise<ParsedImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '' });

        if (json.length < 2) {
          reject(new Error('A planilha deve conter cabeçalho e ao menos uma linha de dados.'));
          return;
        }

        const headers = json[0].map((value) => normalizeText(value));
        const headerIndexes = new Map<string, number>();
        headers.forEach((header, index) => {
          if (header) headerIndexes.set(normalizeKey(header), index);
        });
        const requiredMissing = REQUIRED_IMPORT_HEADERS.filter((header) => !headerIndexes.has(normalizeKey(header)));

        if (requiredMissing.length > 0) {
          reject(new Error(`Cabeçalho inválido. Colunas obrigatórias ausentes: ${requiredMissing.join(' | ')}`));
          return;
        }

        const getValue = (row: any[], header: string) => {
          const index = headerIndexes.get(normalizeKey(header));
          return index === undefined ? '' : row[index];
        };

        const rows = json.slice(1).map((row, index) => {
          const distribuidor = getValue(row, 'Distribuidor');
          const consultor = getValue(row, 'Consultor');
          const coordenador = getValue(row, 'Coordenador');
          const executivoBpc = getValue(row, 'Executivo BPC');
          const executivoHf = getValue(row, 'Executivo HF');
          const regional = getValue(row, 'Regional');
          const mes = getValue(row, 'Mês') || getValue(row, 'Mes');
          const ano = getValue(row, 'Ano');
          const metaHpf = getValue(row, 'Meta HPF');
          const values = [distribuidor, consultor, coordenador, executivoBpc, executivoHf, regional, mes, ano, metaHpf];

          const item: ParsedImportRow = {
            guid: makeLocalGuid(),
            distribuidor: normalizeText(distribuidor),
            consultor: normalizeText(consultor),
            coordenador: normalizeText(coordenador),
            executivoBpc: normalizeText(executivoBpc),
            executivoHf: normalizeText(executivoHf),
            regional: normalizeText(regional),
            mes: finalizeMonthValue(mes),
            ano: finalizeYearValue(ano),
            metaHpf: finalizeMetaValue(metaHpf),
            rowNumber: index + 2,
            valid: true,
          };

          const isEmpty = values.every((value) => normalizeText(value) === '');
          if (isEmpty) {
            return { ...item, valid: false, reason: 'Linha vazia' };
          }

          if (!item.distribuidor) {
            return { ...item, valid: false, reason: 'Distribuidor vazio' };
          }

          if (!item.regional) {
            return { ...item, valid: false, reason: 'Regional vazia' };
          }

          if (item.mes !== '' && parseMonthNumber(item.mes) === null) {
            return { ...item, valid: false, reason: 'Mês inválido' };
          }

          if (item.ano !== '' && parseYearNumber(item.ano) === null) {
            return { ...item, valid: false, reason: 'Ano inválido' };
          }

          if (item.metaHpf !== '' && !Number.isFinite(Number(item.metaHpf))) {
            return { ...item, valid: false, reason: 'Meta HPF inválida' };
          }

          return item;
        });

        resolve(rows.filter((row) => row.reason !== 'Linha vazia'));
      } catch {
        reject(new Error('Não foi possível ler o arquivo XLSX.'));
      }
    };

    reader.onerror = () => reject(new Error('Não foi possível ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

export const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, onImport }) => {
  const [visible, setVisible] = React.useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [rows, setRows] = useState<ParsedImportRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = React.useCallback(() => {
    setFile(null);
    setRows([]);
    setParseError(null);
    setDragging(false);
    setImporting(false);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      reset();
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [isOpen, reset]);

  React.useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !importing) onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, importing, onClose]);

  const validRows = rows.filter((row) => row.valid);
  const invalidRows = rows.filter((row) => !row.valid);

  const counts = useMemo(() => ({
    valid: validRows.length,
    invalid: invalidRows.length,
    total: rows.length,
  }), [validRows.length, invalidRows.length, rows.length]);

  const handleFile = async (nextFile: File) => {
    setParseError(null);
    setRows([]);

    if (!nextFile.name.endsWith('.xlsx') && !nextFile.name.endsWith('.xls')) {
      setParseError('Formato inválido. Envie um arquivo XLSX.');
      return;
    }

    setFile(nextFile);

    try {
      const parsedRows = await parseFile(nextFile);
      setRows(parsedRows);
    } catch (error: any) {
      setParseError(error?.message || 'Não foi possível processar o arquivo.');
    }
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  const handleImport = async () => {
    if (validRows.length === 0 || invalidRows.length > 0) return;
    setImporting(true);

    const success = await onImport(validRows.map(({ rowNumber, valid, reason, ...row }) => row));

    setImporting(false);
    if (success) handleClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 transition-all duration-200 ${
        visible ? 'opacity-100' : 'opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-label="Importar metas Unilever"
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />

      <div
        className={`relative flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-2xl transition-all duration-200 ${
          visible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-3'
        }`}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar Metas Unilever</h2>
            <p className="mt-0.5 text-xs text-gray-500">Modelo XLSX com mês e ano opcionais</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={importing}
            className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-5 overflow-hidden rounded-xl border border-gray-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50">
                  {EXPECTED_HEADERS.map((header) => (
                    <th key={header} className="px-3 py-2 text-left font-semibold text-gray-600">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-500">Andrade - Al</td>
                  <td className="px-3 py-2 text-gray-500">Isaac Lima</td>
                  <td className="px-3 py-2 text-gray-500">Gleicon Santos</td>
                  <td className="px-3 py-2 text-gray-500">Priscilla Guedes</td>
                  <td className="px-3 py-2 text-gray-500">Priscilla Guedes</td>
                  <td className="px-3 py-2 text-gray-500">Nordeste</td>
                  <td className="px-3 py-2 text-gray-500">Janeiro</td>
                  <td className="px-3 py-2 text-gray-500">2026</td>
                  <td className="px-3 py-2 text-right font-mono text-gray-500">8428429.95</td>
                </tr>
              </tbody>
            </table>
          </div>

          {!file && (
            <div
              onDrop={(event) => {
                event.preventDefault();
                setDragging(false);
                const droppedFile = event.dataTransfer.files[0];
                if (droppedFile) handleFile(droppedFile);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragging(false);
              }}
              onClick={() => inputRef.current?.click()}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-10 transition-colors ${
                dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
              }`}
              role="button"
            >
              <div className="mb-3 rounded-xl bg-white p-3 shadow-sm">
                <Upload className="h-7 w-7 text-blue-600" />
              </div>
              <p className="text-sm font-medium text-gray-700">Selecionar XLSX</p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) => {
                  const selectedFile = event.target.files?.[0];
                  if (selectedFile) handleFile(selectedFile);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                className="hidden"
              />
            </div>
          )}

          {parseError && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
                <p className="text-sm text-red-700">{parseError}</p>
              </div>
            </div>
          )}

          {file && rows.length > 0 && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <FileSpreadsheet className="h-4 w-4 shrink-0 text-gray-500" />
                  <span className="truncate text-sm font-medium text-gray-700">{file.name}</span>
                </div>
                <button
                  type="button"
                  onClick={reset}
                  disabled={importing}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Remover
                </button>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                  <p className="text-lg font-bold text-gray-800">{counts.total}</p>
                  <p className="text-xs text-gray-500">Linhas lidas</p>
                </div>
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                  <p className="text-lg font-bold text-emerald-700">{counts.valid}</p>
                  <p className="text-xs text-emerald-600">Válidas</p>
                </div>
                <div className="rounded-xl border border-red-200 bg-red-50 p-3">
                  <p className="text-lg font-bold text-red-700">{counts.invalid}</p>
                  <p className="text-xs text-red-600">Inválidas</p>
                </div>
              </div>

              <div className="max-h-[320px] overflow-auto rounded-xl border border-gray-200">
                <table className="min-w-[1120px] w-full divide-y divide-gray-200 text-xs">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Linha</th>
                      {EXPECTED_HEADERS.map((header) => (
                        <th key={header} className="px-3 py-2 text-left font-semibold text-gray-500">
                          {header}
                        </th>
                      ))}
                      <th className="px-3 py-2 text-left font-semibold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.map((row) => (
                      <tr key={`${row.rowNumber}-${row.distribuidor}`} className={row.valid ? '' : 'bg-red-50/60'}>
                        <td className="px-3 py-2 font-mono text-gray-500">{row.rowNumber}</td>
                        <td className="px-3 py-2 text-gray-700">{row.distribuidor}</td>
                        <td className="px-3 py-2 text-gray-700">{row.consultor}</td>
                        <td className="px-3 py-2 text-gray-700">{row.coordenador}</td>
                        <td className="px-3 py-2 text-gray-700">{row.executivoBpc}</td>
                        <td className="px-3 py-2 text-gray-700">{row.executivoHf}</td>
                        <td className="px-3 py-2 text-gray-700">{row.regional}</td>
                        <td className="px-3 py-2 text-gray-700">{row.mes}</td>
                        <td className="px-3 py-2 text-gray-700">{row.ano}</td>
                        <td className="px-3 py-2 text-right font-mono text-gray-700">{row.metaHpf}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 font-medium ${
                            row.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {row.valid ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                            {row.valid ? 'OK' : row.reason}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 rounded-b-2xl border-t border-gray-200 bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={handleClose}
            disabled={importing}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={validRows.length === 0 || invalidRows.length > 0 || importing}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2 text-sm font-medium text-white shadow-sm shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            Importar
          </button>
        </div>
      </div>
    </div>
  );
};
